import { useState } from "react";
import { bool, func, number, object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { useActiveProjectStore } from "@hooks";
import { TEMPLATE_TYPE } from "@utils";
import { Autocomplete, ControlledTextField, InfoLabel } from "@components";
import { TemplatesDialog } from "@dialogs";
import { XlsImporter, XlsImporterSettingsProvider } from "@tools";
import { TemplateSelector } from "./components";
import { Grid } from "@material-ui/core";

export const ManualCreator = observer(({
  control,
  addCustomWorkType,
  onClose,
  workTypesLoading,
  useTemplatesDialog,
  defaultTemplateId,
}) => {
  const { t } = useTranslation();
  const store = useActiveProjectStore();
  
  const [ templatesDialogOpened, openTemplatesDialog ] = useState(false);
  const [ importDialogOpened, openImportDialog ] = useState(false);
  
  const { availableWorkTypes, projectWorkTypes } = store;
  
  const handleWorkTypesChange = (formChange) => (workTypes, _, removedWorkType) => {
    formChange(workTypes);
    store.setProjectWorkTypes(workTypes);
    if(removedWorkType && (removedWorkType < 0 || typeof removedWorkType === "string")) {
      store.removeWorkType(removedWorkType);
    }
  };
  
  const handleButtonClick = () => {
    useTemplatesDialog ? handleTemplatesDialogOpen() : onClose();
  };
  
  
  const handleTemplatesDialogOpen = () => {
    openTemplatesDialog(!templatesDialogOpened);
  };
  
  const handleTemplatesClose = (type) => {
    if (type === TEMPLATE_TYPE.XLS) openImportDialog(true);
    openTemplatesDialog(false);
  };
  
  const handleXlsImport = (fileName, importedStructure, importedWorkTypes) => {
    importedWorkTypes.forEach((wT) => {
      store.addWorkType(wT);
    })
    
    store.setXlsProjectStructure(importedStructure, fileName);
    store.setProjectWorkTypes(importedWorkTypes.map(({id}) => id));
    openImportDialog(false);
  }
  
  const handleXlsImportCancel = () => {
    openImportDialog(false);
  }
  
  return (
    <Grid
      item
      container
      direction="column"
      wrap="nowrap"
    >
      <InfoLabel spacing={8} label="views.new_project.name" />
      <ControlledTextField
        control={control}
        name="name"
        placeholder={t("views.new_project.enter_name")}
      />
      
      <InfoLabel spacing={8} label="views.new_project.desc" />
      <ControlledTextField
        control={control}
        name="description"
        multiline
        placeholder={t("views.new_project.enter_desc")}
        minRows={2}
      />
      
      <InfoLabel spacing={8} label="common.template" />
      <TemplateSelector onClick={handleButtonClick} />
      {
        useTemplatesDialog &&
        <TemplatesDialog
          open={templatesDialogOpened}
          onClose={handleTemplatesClose}
          defaultTemplateId={defaultTemplateId}
        />
      }
      
      <InfoLabel
        spacing={8}
        label="common.work_type"
        tooltipText="views.new_project.work_type_desc"
      />
      <Controller
        name="workTypes"
        control={control}
        render={({
          field: { onChange, name },
          fieldState: { error },
        }) => (
          <Autocomplete
            id="workTypeSelect"
            value={projectWorkTypes}
            options={availableWorkTypes}
            fullWidth
            multiple
            idKey="id"
            labelKey="name"
            onChange={handleWorkTypesChange(onChange)}
            onOptionCreate={addCustomWorkType}
            name={name}
            dataLoading={workTypesLoading}
            error={!!error}
            helperText={error?.message}
            inputProps={{ className: "mb-0" }}
            inputPlaceholder={t("views.new_project.enter_system")}
            getOptionDisabled={() => projectWorkTypes.length >= 10}
          />
        )}
      />
      {/*{ @todo: uncomment when fixed on Backend */}
      {/*  template?.hasComments &&*/}
      {/*  <>*/}
      {/*    <Divider className="my-0 -mx-6" />*/}
      {/*    <Grid item container xs={12} className="my-4">*/}
      {/*      <InfoLabel label="forms.template.comments_template.short" />*/}
      {/*      */}
      {/*      <ControlledCheckbox*/}
      {/*        name="copyComments"*/}
      {/*        control={control}*/}
      {/*        label={t("forms.template.comments_template.yes")}*/}
      {/*      />*/}
      {/*    </Grid>*/}
      {/*  </>*/}
      {/*}*/}
      
      {importDialogOpened && (
        <XlsImporterSettingsProvider workTypes={store.workTypes}>
          <XlsImporter
            modalOpened={importDialogOpened}
            onImportCancel={handleXlsImportCancel}
            onImportFinish={handleXlsImport}
          />
        </XlsImporterSettingsProvider>
      )}
    </Grid>
  );
});

ManualCreator.propTypes = {
  control: object.isRequired,
  setValue: func.isRequired,
  addCustomWorkType: func.isRequired,
  onClose: func.isRequired,
  useTemplatesDialog: bool,
  defaultTemplateId: number,
};